import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Typography,
  AppBar,
  Toolbar,
  Button,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  Fade,
  Card,
  CardContent,
  Container,
  CircularProgress,
  TextField,
  Backdrop,
  Grid,
  Snackbar,
  Alert,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate } from 'react-router-dom';
import CryptoJS from 'crypto-js';
import axios from 'axios';
import { FaChartLine, FaUsers, FaUser, FaSignOutAlt, FaBars, FaSyncAlt, FaArrowLeft, FaArrowRight } from 'react-icons/fa';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { format, subMonths } from 'date-fns';
import { Assignment, Person, EmojiEvents, People, CalendarToday, CropSquare, ViewModule, GridView } from '@mui/icons-material';
import i18n from '../i18n';
import LanguageSwitcher from '../components/LanguageSwitcher';

const ENCRYPTION_KEY = 'dadash123@@123##';

const Signals = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [user, setUser] = useState({});
  const [mobileOpen, setMobileOpen] = useState(false);
  const [signals, setSignals] = useState([]);
  const [filteredSignals, setFilteredSignals] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pageNumber, setPageNumber] = useState(0);
  const [startDate, setStartDate] = useState(subMonths(new Date(), 1));
  const [endDate, setEndDate] = useState(new Date());
  const [searchText, setSearchText] = useState('');
  const [cardsPerRow, setCardsPerRow] = useState(1);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('info');
  const stickyRef = useRef(null);

  useEffect(() => {
    // console.log(`cardsPerRow changed to: ${cardsPerRow}`);
  }, [cardsPerRow]);

  useEffect(() => {
    const encryptedData = localStorage.getItem('user');
    if (encryptedData) {
      try {
        const bytes = CryptoJS.AES.decrypt(encryptedData, ENCRYPTION_KEY);
        const decrypted = bytes.toString(CryptoJS.enc.Utf8);
        setUser(JSON.parse(decrypted));
      } catch (err) {
        localStorage.removeItem('user');
        navigate('/');
      }
    } else {
      navigate('/');
    }
  }, [navigate]);

  useEffect(() => {
    if (user.UserID) {
      fetchSignals();
    }
  }, [user, pageNumber, startDate, endDate]);

  useEffect(() => {
    if (searchText) {
      const lowerSearch = searchText.trim().toLowerCase();
      // console.log('Search text:', lowerSearch);
      const filtered = signals.filter((signal) => {
        const { Symbol, Action, Price, Ticket, Profit, restFields } = parseJsonText(signal.JsonText);

        // برای مقایسه با WIN
        const winString = Profit?.toString().trim();
        let matchesWin = false;
        if (lowerSearch === 'Profit') {
          matchesWin = Profit !== undefined;
        } else {
          matchesWin = winString && winString.includes(searchText.trim());
        }

        const matchesTicket = Ticket?.toString().toLowerCase().includes(lowerSearch) || false;
        const matchesSymbol = Symbol?.toLowerCase().includes(lowerSearch) || false;
        const matchesAction = Action?.toLowerCase().includes(lowerSearch) || false;
        const matchesPrice = Price?.toString().toLowerCase().includes(lowerSearch) || false;
        const matchesReceiveCount = signal.ReceiveCount?.toString().toLowerCase().includes(lowerSearch) || false;
        const matchesRestFields = restFields?.toLowerCase().includes(lowerSearch) || false;
        const matchesKindNormal = signal.Kind === 3 && t('signals.kind.normal').toLowerCase().includes(lowerSearch);
        const matchesKindPremium = signal.Kind === 4 && t('signals.kind.premium').toLowerCase().includes(lowerSearch);
        const matchesDateTime = formatDateTime(signal.ReceivedDatetime).toLowerCase().includes(lowerSearch);

        return (
          matchesTicket ||
          matchesSymbol ||
          matchesAction ||
          matchesPrice ||
          matchesWin ||
          matchesReceiveCount ||
          matchesRestFields ||
          matchesKindNormal ||
          matchesKindPremium ||
          matchesDateTime
        );
      });
      setFilteredSignals(filtered);
      if (filtered.length === 0) {
        setSnackbarMessage(t('signals.noResults'));
        setSnackbarSeverity('warning');
        setSnackbarOpen(true);
      }
    } else {
      setFilteredSignals(signals);
      if (signals.length === 0 && !loading) {
        setSnackbarMessage(t('signals.noData'));
        setSnackbarSeverity('info');
        setSnackbarOpen(true);
      }
    }
  }, [searchText, signals, t, loading]);

  const fetchSignals = async () => {
    setLoading(true);
    setSnackbarOpen(false);

    try {
      const response = await axios.post(
        'https://copyapi.avangar.ir/api/forex/SignalCreate',
        {
          parentId: user.UserID,
          startDate: startDate ? format(startDate, 'yyyy-MM-dd') : '',
          endDate: endDate ? format(endDate, 'yyyy-MM-dd') : '',
          PageNumber: pageNumber,
          rowsPerPage: 48,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
          },
        }
      );
      setSignals(response.data || []);
      // console.log('API signals received:', response.data);
    } catch (err) {
      setSnackbarMessage(err.response?.data?.Message || t('signals.error'));
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      setSignals([]);
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('user');
    navigate('/');
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handlePreviousPage = () => {
    if (pageNumber > 0) {
      setPageNumber(pageNumber - 1);
    }
  };

  const handleNextPage = () => {
    setPageNumber(pageNumber + 1);
  };

  const handleSnackbarClose = () => {
    setSnackbarOpen(false);
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return format(date, 'yyyy-MM-dd HH:mm:ss:SSS');
  };

  const getKindLabel = (kind) => {
    if (kind === 3) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><Person sx={{ color: '#4caf50' }} />{t('signals.kind.normal')}</Box>;
    if (kind === 4) return <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}><EmojiEvents sx={{ color: '#ffb300' }} />{t('signals.kind.premium')}</Box>;
    return '-';
  };

  const parseJsonText = (jsonText) => {
    try {
      const data = JSON.parse(jsonText);
      const { Symbol, Action, Price, Ticket, Profit, ...rest } = data;
      const restFields = Object.entries(rest)
        .map(([key, value]) => `${key}: ${value}`)
        .join(', ');
      // console.log('Parsed JsonText:', { jsonText, Symbol, Action, Price, Ticket, WIN, restFields });
      return { Symbol, Action, Price, Ticket, Profit, restFields: restFields || '-' };
    } catch (err) {
      console.error('Error parsing JsonText:', err, jsonText);
      return { Symbol: '-', Action: '-', Price: '-', Ticket: '-', Profit: undefined, restFields: t('signals.invalidData') };
    }
  };

  const menuItems = [
    { text: t('home') || 'Home', icon: <FaChartLine />, path: '/home' },
    { text: t('subscribers') || 'Subscribers', icon: <FaUsers />, path: '/subscribers' },
    { text: t('signals.title') || 'Signals', icon: <FaChartLine />, path: '/signals' },
    { text: t('profile.title') || 'Profile', icon: <FaUser />, path: '/profile' },
  ];

  const drawer = (
    <Box sx={{ p: 2, bgcolor: '#fff' }}>
      <List>
        {menuItems.map((item) => (
          <ListItem
            key={item.path}
            component={Link}
            to={item.path}
            onClick={handleDrawerToggle}
            sx={{
              py: 1,
              '&:hover': {
                bgcolor: 'rgba(0,255,0,0.1)',
                boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                transform: 'scale(1.02)',
              },
              transition: 'all 0.2s ease-in-out',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ color: '#1e3c72' }}>{item.icon}</Box>
              <ListItemText primary={item.text} />
            </Box>
          </ListItem>
        ))}
        <ListItem
          onClick={handleLogout}
          sx={{
            py: 1,
            '&:hover': {
              bgcolor: 'rgba(255,0,0,0.1)',
              boxShadow: '0 0 8px rgba(255,0,0,0.3)',
              transform: 'scale(1.02)',
            },
            transition: 'all 0.2s ease-in-out',
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ color: '#1e3c72' }}><FaSignOutAlt /></Box>
            <ListItemText primary={t('logout')} />
          </Box>
        </ListItem>
      </List>
    </Box>
  );

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(45deg, #1e3c72, #2a5298, rgba(0,255,0,0.3), rgba(255,0,0,0.3))',
        backgroundSize: '400% 400%',
        animation: 'backgroundGradient 15s ease infinite',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '10%',
          left: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(0, 255, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out',
        }}
      />
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '5%',
          width: '200px',
          height: '200px',
          background: 'radial-gradient(circle, rgba(255, 0, 0, 0.3) 0%, transparent 70%)',
          animation: 'pulse 6s infinite ease-in-out reverse',
        }}
      />
      <style>
        {`
          @keyframes backgroundGradient {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes pulse {
            0% { transform: scale(1); opacity: 0.5; }
            50% { transform: scale(1.2); opacity: 0.3; }
            100% { transform: scale(1); opacity: 0.5; }
          }
          @keyframes slideInLeft {
            0% { transform: translateX(-100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes slideInRight {
            0% { transform: translateX(100px); opacity: 0; }
            100% { transform: translateX(0); opacity: 1; }
          }
          @keyframes vibrate {
            0% { transform: translateX(0); }
            20% { transform: translateX(-2px); }
            40% { transform: translateX(2px); }
            60% { transform: translateX(-2px); }
            80% { transform: translateX(2px); }
            100% { transform: translateX(0); }
          }
          @keyframes winPulseGreen {
            0% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(0, 255, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(0, 255, 0, 0.5); }
          }
          @keyframes winPulseRed {
            0% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
            50% { box-shadow: 0 0 15px 5px rgba(255, 0, 0, 0.3); }
            100% { box-shadow: 0 0 0 0 rgba(255, 0, 0, 0.5); }
          }
          @keyframes slideDown {
            0% { transform: translateY(-50px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes gradientMove {
            0% { background-position: 0% 50%; }
            50% { background-position: 100% 50%; }
            100% { background-position: 0% 50%; }
          }
          @keyframes neonGlowGreen {
            0% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(0, 255, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(0, 255, 0, 0.3); }
          }
          @keyframes neonGlowRed {
            0% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { box-shadow: 0 0 20px rgba(255, 0, 0, 0.7); }
            100% { box-shadow: 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes softBounce {
            0% { transform: scale(1); }
            50% { transform: scale(0.95); }
            100% { transform: scale(1); }
          }
          @keyframes rotateIcon {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
          @keyframes ripple {
            0% { box-shadow: 0 0 0 0 rgba(255, 255, 255, 0.5); }
            100% { box-shadow: 0 0 0 20px rgba(255, 255, 255, 0); }
          }
          @keyframes typing {
            0% { width: 0; }
            100% { width: 100%; }
          }
          @keyframes glow {
            0% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
            50% { text-shadow: 0 0 15px rgba(0, 255, 0, 0.7), 0 0 15px rgba(255, 0, 0, 0.7); }
            100% { text-shadow: 0 0 5px rgba(0, 255, 0, 0.3), 0 0 5px rgba(255, 0, 0, 0.3); }
          }
          @keyframes fadeInDown {
            0% { transform: translateY(-20px); opacity: 0; }
            100% { transform: translateY(0); opacity: 1; }
          }
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
      <Backdrop
        sx={{ color: '#fff', zIndex: (theme) => theme.zIndex.drawer + 1, bgcolor: 'rgba(0,0,0,0.5)' }}
        open={loading}
      >
        <CircularProgress
          size={60}
          thickness={4}
          sx={{
            color: 'transparent',
            background: 'linear-gradient(90deg, #00ff00, #ff0000)',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite',
          }}
        />
      </Backdrop>
      <AppBar
        position="static"
        key={`appbar-${i18n.language}`}
        sx={{
          bgcolor: 'rgba(255,255,255,0.95)',
          boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
          zIndex: 1300,
        }}
      >
        <Toolbar sx={{ justifyContent: 'space-between' }}>
          <Typography
            variant="h6"
            key={`title-${i18n.language}`}
            sx={{
              color: '#1e3c72',
              fontWeight: 'bold',
              textShadow: '0 0 8px rgba(0,255,0,0.2)',
              animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
            }}
          >
            {t('signals.title')}
          </Typography>
          <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 2, direction: i18n.language === 'fa' ? 'rtl' : 'ltr' }}>
            {menuItems.map((item) => (
              <Button
                key={item.path}
                component={Link}
                to={item.path}
                startIcon={item.icon}
                sx={{
                  color: '#1e3c72',
                  textTransform: 'none',
                  fontSize: '1rem',
                  '&:hover': {
                    color: '#fff',
                    bgcolor: '#2a5298',
                    boxShadow: '0 0 10px rgba(0,255,0,0.4), 0 0 10px rgba(255,0,0,0.4)',
                    transform: 'scale(1.05)',
                  },
                  transition: 'all 0.2s ease-in-out',
                }}
              >
                {item.text}
              </Button>
            ))}
            <Button
              startIcon={<FaSignOutAlt />}
              onClick={handleLogout}
              sx={{
                color: '#1e3c72',
                textTransform: 'none',
                fontSize: '1rem',
                '&:hover': {
                  color: '#fff',
                  bgcolor: '#d32f2f',
                  boxShadow: '0 0 10px rgba(255,0,0,0.5)',
                  transform: 'scale(1.05)',
                },
                transition: 'all 0.2s ease-in-out',
              }}
            >
              {t('logout')}
            </Button>
            <LanguageSwitcher />
          </Box>
          <IconButton
            color="inherit"
            edge="end"
            onClick={handleDrawerToggle}
            sx={{ display: { md: 'none' }, color: '#1e3c72' }}
          >
            <FaBars />
          </IconButton>
        </Toolbar>
      </AppBar>
      <Drawer
        anchor={i18n.language === 'fa' ? 'right' : 'left'}
        open={mobileOpen}
        onClose={handleDrawerToggle}
        sx={{ '& .MuiDrawer-paper': { width: 240 } }}
      >
        {drawer}
      </Drawer>
      <Box
        ref={stickyRef}
        sx={{
          position: 'sticky',
          top: 0,
          zIndex: 1200,
          bgcolor: 'rgba(255,255,255,0.95)',
          py: { xs: 0.3, sm: 2 },
          px: { xs: 0.5, sm: 4 },
          boxShadow: '0 2px 8px rgba(0,0,0,0.1), 0 0 10px rgba(0,255,0,0.2), 0 0 10px rgba(255,0,0,0.2)',
          borderRadius: 2,
          width: '100%',
          boxSizing: 'border-box',
          maxWidth: { xs: '100%', sm: 'lg' },
          mx: 'auto',
          contain: 'layout',
          animation: 'slideDown 0.5s ease-out forwards',
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Grid container spacing={{ xs: 1, sm: 1 }} alignItems="center" sx={{ flexWrap: 'wrap' }}>
            <Grid item xs={12} sm={6}>
              <Box sx={{ contain: 'layout', position: 'static', width: { xs: '231px', sm: '200px' } }}>
                <TextField
                  fullWidth
                  label={t('signals.search')}
                  value={searchText}
                  onChange={(e) => setSearchText(e.target.value)}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      '& fieldset': { borderColor: '#2a5298' },
                      '&:hover fieldset': { borderColor: '#1e3c72' },
                      '&.Mui-focused fieldset': {
                        borderColor: '#1e3c72',
                        boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                      },
                      fontSize: { xs: '0.8rem', sm: '1rem' },
                      lineHeight: { xs: '1', sm: '1.5' },
                      height: { xs: '32px', sm: '56px' },
                      py: { xs: 0, sm: 1 },
                      transform: 'none',
                      position: 'static',
                    },
                    '& .MuiInputBase-root': {
                      transform: 'none',
                      position: 'static',
                    },
                    '& .MuiInputLabel-root': {
                      fontSize: { xs: '0.7rem', sm: '1rem' },
                      transform: { xs: 'translate(14px, 8px) scale(1)', sm: 'translate(14px, 16px) scale(1)' },
                      '&.MuiInputLabel-shrink': {
                        transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                      },
                      lineHeight: 1,
                    },
                    '& .Mui-focused .MuiInputLabel-root': {
                      transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                    },
                  }}
                />
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  contain: 'layout',
                  position: 'static',
                  display: 'flex',
                  flexDirection: 'row',
                  gap: { xs: 1, sm: 1 },
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'nowrap',
                }}
              >
                <Box
                  sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    gap: { xs: 1, sm: 1 },
                    justifyContent: i18n.language === 'fa' ? 'flex-end' : 'flex-start',
                    flexWrap: 'nowrap',
                  }}
                >
                  <DatePicker
                    label={t('signals.fromDate')}
                    value={startDate}
                    onChange={(newValue) => setStartDate(newValue)}
                    format="yyyy-MM-dd"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        sx: {
                          width: { xs: '110px', sm: '200px' },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#2a5298' },
                            '&:hover fieldset': { borderColor: '#1e3c72' },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1e3c72',
                              boxShadow: '0 0 8px rgba(0,255,0,0.3)',
                            },
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            lineHeight: { xs: '1', sm: '1.5' },
                            height: { xs: '32px', sm: '56px' },
                            py: { xs: 0, sm: 1 },
                            px: { xs: 1, sm: 2 },
                            transform: 'none',
                            position: 'static',
                          },
                          '& .MuiInputBase-root': {
                            transform: 'none',
                            position: 'static',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.7rem', sm: '1rem' },
                            transform: { xs: 'translate(14px, 8px) scale(1)', sm: 'translate(14px, 16px) scale(1)' },
                            '&.MuiInputLabel-shrink': {
                              transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                            },
                            lineHeight: 1,
                          },
                          '& .Mui-focused .MuiInputLabel-root': {
                            transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                          },
                        },
                      },
                    }}
                  />
                  <DatePicker
                    label={t('signals.toDate')}
                    value={endDate}
                    onChange={(newValue) => setEndDate(newValue)}
                    format="yyyy-MM-dd"
                    slotProps={{
                      textField: {
                        variant: 'outlined',
                        sx: {
                          width: { xs: '110px', sm: '200px' },
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '& fieldset': { borderColor: '#2a5298' },
                            '&:hover fieldset': { borderColor: '#1e3c72' },
                            '&.Mui-focused fieldset': {
                              borderColor: '#1e3c72',
                              boxShadow: '0 0 8px rgba(255,0,0,0.3)',
                            },
                            fontSize: { xs: '0.8rem', sm: '1rem' },
                            lineHeight: { xs: '1', sm: '1.5' },
                            height: { xs: '32px', sm: '56px' },
                            py: { xs: 0, sm: 1 },
                            px: { xs: 1, sm: 2 },
                            transform: 'none',
                            position: 'static',
                          },
                          '& .MuiInputBase-root': {
                            transform: 'none',
                            position: 'static',
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.7rem', sm: '1rem' },
                            transform: { xs: 'translate(14px, 8px) scale(1)', sm: 'translate(14px, 16px) scale(1)' },
                            '&.MuiInputLabel-shrink': {
                              transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                            },
                            lineHeight: 1,
                          },
                          '& .Mui-focused .MuiInputLabel-root': {
                            transform: { xs: 'translate(14px, -6px) scale(0.75)', sm: 'translate(14px, -6px) scale(0.75)' },
                          },
                        },
                      },
                    }}
                  />
                  <IconButton
                    onClick={fetchSignals}
                    disabled={loading}
                    sx={{
                      color: '#1e3c72',
                      bgcolor: 'transparent',
                      borderRadius: 2,
                      width: { xs: '32px', sm: '56px' },
                      height: { xs: '32px', sm: '56px' },
                      padding: { xs: '4px', sm: '6px' },
                      '&:hover': {
                        bgcolor: '#e0f7fa',
                        transform: 'scale(1.1)',
                      },
                      transition: 'all 0.2s ease-in-out',
                    }}
                  >
                    {loading ? <CircularProgress size={18} color="inherit" /> : <FaSyncAlt size={18} />}
                  </IconButton>
                </Box>
                <Box
                  sx={{
                    display: { xs: 'none', md: 'flex' },
                    gap: 1,
                    justifyContent: i18n.language === 'fa' ? 'flex-start' : 'flex-end',
                  }}
                >
                  {[1, 2, 4].map((num) => (
                    <IconButton
                      key={num}
                      onClick={() => {
                        setCardsPerRow(num);
                        // console.log(`cardsPerRow set to: ${num}`);
                      }}
                      sx={{
                        color: cardsPerRow === num ? '#fff' : '#1e3c72',
                        bgcolor: cardsPerRow === num ? '#1e3c72' : 'transparent',
                        borderRadius: 2,
                        width: { xs: '32px', sm: '56px' },
                        height: { xs: '32px', sm: '56px' },
                        padding: { xs: '4px', sm: '6px' },
                        '&:hover': {
                          bgcolor: cardsPerRow === num ? '#1e3c72' : 'rgba(0,255,0,0.1)',
                          transform: 'scale(1.05)',
                        },
                        transition: 'all 0.2s ease-in-out',
                      }}
                    >
                      {num === 1 ? (
                        <CropSquare sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                      ) : num === 2 ? (
                        <ViewModule sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                      ) : (
                        <GridView sx={{ fontSize: { xs: '18px', sm: '24px' } }} />
                      )}
                    </IconButton>
                  ))}
                </Box>
              </Box>
            </Grid>
          </Grid>
        </LocalizationProvider>
        <Box
          sx={{
            mt: { xs: 0.3, sm: 2 },
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            px: { xs: 0.5, sm: 0 },
          }}
        >
          <IconButton
            onClick={handlePreviousPage}
            disabled={pageNumber === 0 || loading}
            sx={{
              color: '#1e3c72',
              borderRadius: 2,
              padding: { xs: '2px', sm: '8px' },
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'rgba(0,255,0,0.1)',
                animation: 'neonGlowGreen 1s infinite',
              },
              '&:active': {
                animation: 'softBounce 0.3s ease-in-out',
              },
              transition: 'all 0.2s ease-in-out',
              display: { xs: 'inline-flex', sm: 'none' },
            }}
          >
            <FaArrowLeft size={14} />
          </IconButton>
          <Button
            variant="outlined"
            onClick={handlePreviousPage}
            disabled={pageNumber === 0 || loading}
            sx={{
              borderRadius: 2,
              color: '#1e3c72',
              borderColor: '#2a5298',
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '1rem' },
              padding: { xs: '2px 6px', sm: '6px 16px' },
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'rgba(0,255,0,0.1)',
                borderColor: '#1e3c72',
                animation: 'neonGlowGreen 1s infinite',
              },
              '&:active': {
                animation: 'softBounce 0.3s ease-in-out',
              },
              transition: 'all 0.2s ease-in-out',
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {t('signals.previous')}
          </Button>
          <Typography sx={{ color: '#1e3c72', fontWeight: 'bold', fontSize: { xs: '0.7rem', sm: '1rem' } }}>
            {t('signals.page')}: {pageNumber + 1}
          </Typography>
          <IconButton
            onClick={handleNextPage}
            disabled={loading || signals.length < 20}
            sx={{
              color: '#1e3c72',
              borderRadius: 2,
              padding: { xs: '2px', sm: '8px' },
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'rgba(255,0,0,0.1)',
                animation: 'neonGlowRed 1s infinite',
              },
              '&:active': {
                animation: 'softBounce 0.3s ease-in-out',
              },
              transition: 'all 0.2s ease-in-out',
              display: { xs: 'inline-flex', sm: 'none' },
            }}
          >
            <FaArrowRight size={14} />
          </IconButton>
          <Button
            variant="outlined"
            onClick={handleNextPage}
            disabled={loading || signals.length < 20}
            sx={{
              borderRadius: 2,
              color: '#1e3c72',
              borderColor: '#2a5298',
              textTransform: 'none',
              fontSize: { xs: '0.8rem', sm: '1rem' },
              padding: { xs: '2px 6px', sm: '6px 16px' },
              position: 'relative',
              overflow: 'hidden',
              '&:hover': {
                background: 'rgba(255,0,0,0.1)',
                borderColor: '#1e3c72',
                animation: 'neonGlowRed 1s infinite',
              },
              '&:active': {
                animation: 'softBounce 0.3s ease-in-out',
              },
              transition: 'all 0.2s ease-in-out',
              display: { xs: 'none', sm: 'inline-flex' },
            }}
          >
            {t('signals.next')}
          </Button>
        </Box>
      </Box>
      <Container
        sx={{
          py: { xs: 1, sm: 2 },
          px: { xs: 0, sm: 0 },
          width: '100%',
          maxWidth: '100% !important',
          boxSizing: 'border-box',
        }}
      >
        <Fade in timeout={600}>
          <Card
            sx={{
              borderRadius: 3,
              boxShadow: `
                0 8px 24px rgba(0,0,0,0.2),
                0 0 20px rgba(0,255,0,0.2),
                0 0 20px rgba(255,0,0,0.2)
              `,
              bgcolor: 'rgba(255,255,255,0.95)',
              width: '100%',
              boxSizing: 'border-box',
            }}
          >
            <CardContent sx={{ p: { xs: 1, sm: 2 }, width: '100%', boxSizing: 'border-box' }}>
              <Box sx={{ overflow: 'hidden', whiteSpace: 'nowrap' }}>
                <Typography
                  variant="h4"
                  sx={{
                    color: '#1e3c72',
                    fontWeight: 'bold',
                    mb: 3,
                    textShadow: '0 0 10px rgba(0,255,0,0.3)',
                    textAlign: 'center',
                    fontSize: { xs: '1.5rem', sm: '2rem' },
                    display: 'inline-block',
                    animation: 'typing 2s steps(30, end) forwards, glow 2s infinite',
                  }}
                >
                  {t('signals.title')}
                </Typography>
              </Box>
              <Box sx={{ mt: 3, width: '100%' }}>
                {filteredSignals.length > 0 ? (
                  <Box
                    sx={{
                      display: 'flex',
                      flexWrap: 'wrap',
                      gap: 2,
                      width: '100%',
                      boxSizing: 'border-box',
                      overflowX: 'auto',
                      px: 0,
                    }}
                  >
                    {filteredSignals.map((signal, index) => {
                      const { Symbol, Action, Price, Ticket, Profit, restFields } = parseJsonText(signal.JsonText);
                      const gapTotal = (cardsPerRow - 1) * 16;
                      const cardWidth =
                        cardsPerRow === 1
                          ? '100%'
                          : cardsPerRow === 2
                          ? `calc((100% - ${gapTotal}px) / 2)`
                          : `calc((100% - ${gapTotal}px) / 4)`;
                      // console.log(`Card ${index}: cardsPerRow=${cardsPerRow}, width=${cardWidth}`);
                      return (
                        <Box
                          key={index}
                          sx={{
                            width: { xs: '100%', md: cardWidth },
                            minWidth: { xs: '100%', md: '250px' },
                            animation: i18n.language === 'fa' ? 'slideInRight 0.5s ease-out forwards' : 'slideInLeft 0.5s ease-out forwards',
                            animationDelay: `${index * 0.1}s`,
                            opacity: 0,
                            boxSizing: 'border-box',
                            flexShrink: 0,
                          }}
                        >
                          <Card
                            sx={{
                              borderRadius: 2,
                              bgcolor: Profit !== undefined ? (Profit >= 0 ? '#e8f5e9' : '#ffebee') : 'rgba(255,255,255,0.9)',
                              background: Profit !== undefined ? (Profit >= 0 ? 'linear-gradient(45deg, rgba(0,255,0,0.1), rgba(255,255,255,0.9))' : 'linear-gradient(45deg, rgba(255,0,0,0.1), rgba(255,255,255,0.9))') : 'linear-gradient(45deg, rgba(255,255,255,0.9), rgba(255,255,255,0.9))',
                              backgroundSize: '200% 200%',
                              animation: 'gradientMove 10s ease infinite',
                              boxShadow: `
                                0 4px 12px rgba(0,0,0,0.1),
                                0 0 10px rgba(0,255,0,0.2),
                                0 0 10px rgba(255,0,0,0.2)
                              `,
                              border: searchText && (
                                (Ticket?.toString().toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (Symbol?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (Action?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (Price?.toString().toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (Profit?.toString().trim().includes(searchText.trim()) || (searchText.toLowerCase() === 'profit' && Profit !== undefined)) ||
                                (signal.ReceiveCount?.toString().toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (restFields?.toLowerCase().includes(searchText.toLowerCase()) || false) ||
                                (signal.Kind === 3 && t('signals.kind.normal').toLowerCase().includes(searchText.toLowerCase())) ||
                                (signal.Kind === 4 && t('signals.kind.premium').toLowerCase().includes(searchText.toLowerCase())) ||
                                formatDateTime(signal.ReceivedDatetime).toLowerCase().includes(searchText.toLowerCase())
                              ) ? '2px solid #4caf50' : 'none',
                              '&:hover': {
                                transform: 'translateY(-5px)',
                                boxShadow: `
                                  0 8px 20px rgba(0,0,0,0.2),
                                  0 0 20px rgba(0,255,0,0.4),
                                  0 0 20px rgba(255,0,0,0.4)
                                `,
                                animation: 'vibrate 0.3s ease-in-out',
                                '& .MuiTypography-root': {
                                  color: Profit !== undefined ? (Profit >= 0 ? '#4caf50' : '#d32f2f') : '#1e3c72',
                                },
                              },
                              transition: 'all 0.3s ease-in-out',
                              height: '100%',
                              width: '100%',
                              boxSizing: 'border-box',
                            }}
                          >
                            <CardContent sx={{ p: 1, width: '100%', boxSizing: 'border-box' }}>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                  }}
                                >
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <Assignment sx={{ color: '#1e3c72', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                        display: { xs: 'none', sm: 'inline' },
                                        animation: 'fadeInDown 0.5s ease-out forwards',
                                      }}
                                    >
                                      {t('signals.ticket', { nsSeparator: false })}: 
                                    </Typography>
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                        textAlign: { xs: 'left', sm: 'inherit' },
                                        animation: 'fadeInDown 0.5s ease-out forwards',
                                      }}
                                    >
                                      {Ticket || '-'}
                                    </Typography>
                                  </Box>
                                  {Profit !== undefined && (
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.9rem', sm: '1.1rem' },
                                        bgcolor: Profit >= 0 ? 'rgba(0,255,0,0.1)' : 'rgba(255,0,0,0.1)',
                                        borderRadius: '12px',
                                        px: 1,
                                        py: 0.2,
                                        animation: Profit >= 0 ? 'winPulseGreen 1.5s infinite' : 'winPulseRed 1.5s infinite',
                                      }}
                                    >
                                      Profit: {Profit}
                                    </Typography>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 'bold',
                                      color: '#1e3c72',
                                      fontSize: { xs: '0.8rem', sm: '1rem' },
                                      animation: 'fadeInDown 0.5s ease-out forwards',
                                      animationDelay: '0.1s',
                                    }}
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: { xs: 'none', sm: 'inline' },
                                      }}
                                    >
                                      {t('signals.symbol', { nsSeparator: false })}: 
                                    </Typography>
                                    {Symbol || '-'}
                                  </Typography>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
                                    <CalendarToday sx={{ color: '#0288d1', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                        textAlign: { xs: 'right', sm: 'inherit' },
                                        animation: 'fadeInDown 0.5s ease-out forwards',
                                        animationDelay: '0.1s',
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          display: { xs: 'none', sm: 'inline' },
                                        }}
                                      >
                                        {t('signals.createdAt', { nsSeparator: false })}: 
                                      </Typography>
                                      {formatDateTime(signal.ReceivedDatetime)}
                                    </Typography>
                                  </Box>
                                </Box>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      fontWeight: 'bold',
                                      color: '#1e3c72',
                                      fontSize: { xs: '0.8rem', sm: '1rem' },
                                      animation: 'fadeInDown 0.5s ease-out forwards',
                                      animationDelay: '0.2s',
                                    }}
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: { xs: 'none', sm: 'inline' },
                                      }}
                                    >
                                      {t('signals.action', { nsSeparator: false })}: 
                                    </Typography>
                                    {Action || '-'}
                                  </Typography>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#1e3c72',
                                      fontSize: { xs: '0.8rem', sm: '1rem' },
                                      animation: 'fadeInDown 0.5s ease-out forwards',
                                      animationDelay: '0.2s',
                                    }}
                                  >
                                    <Typography
                                      component="span"
                                      sx={{
                                        display: { xs: 'inline', sm: 'inline' },
                                      }}
                                    >
                                      {t('signals.price', { nsSeparator: false })}: 
                                    </Typography>
                                    {Price || '-'}
                                  </Typography>
                                </Box>
                                <Typography
                                  variant="body1"
                                  sx={{
                                    color: '#1e3c72',
                                    fontSize: { xs: '0.8rem', sm: '1rem' },
                                    animation: 'fadeInDown 0.5s ease-out forwards',
                                    animationDelay: '0.3s',
                                  }}
                                >
                                  {restFields}
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <People sx={{ color: '#d32f2f', fontSize: { xs: '1rem', sm: '1.2rem' } }} />
                                    <Typography
                                      variant="body1"
                                      sx={{
                                        fontWeight: 'bold',
                                        color: '#1e3c72',
                                        fontSize: { xs: '0.8rem', sm: '1rem' },
                                        textAlign: { xs: 'left', sm: 'inherit' },
                                        animation: 'fadeInDown 0.5s ease-out forwards',
                                        animationDelay: '0.4s',
                                      }}
                                    >
                                      <Typography
                                        component="span"
                                        sx={{
                                          display: { xs: 'none', sm: 'inline' },
                                        }}
                                      >
                                        {t('signals.receiveCount', { nsSeparator: false })}: 
                                      </Typography>
                                      {signal.ReceiveCount || '-'}
                                    </Typography>
                                  </Box>
                                  <Typography
                                    variant="body1"
                                    sx={{
                                      color: '#1e3c72',
                                      fontSize: { xs: '0.8rem', sm: '1rem' },
                                      animation: 'fadeInDown 0.5s ease-out forwards',
                                      animationDelay: '0.4s',
                                    }}
                                  >
                                    {getKindLabel(signal.Kind)}
                                  </Typography>
                                </Box>
                              </Box>
                            </CardContent>
                          </Card>
                        </Box>
                      );
                    })}
                  </Box>
                ) : null}
              </Box>
            </CardContent>
          </Card>
        </Fade>
      </Container>
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={4000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiSnackbarContent-root': {
            borderRadius: 2,
            boxShadow: '0 4px 12px rgba(0,0,0,0.2)',
            animation: 'fadeInDown 0.5s ease-out forwards',
          },
        }}
      >
        <Alert
          onClose={handleSnackbarClose}
          severity={snackbarSeverity}
          sx={{
            width: '100%',
            bgcolor: snackbarSeverity === 'success' ? '#e8f5e9' : '#ffebee',
            color: snackbarSeverity === 'success' ? '#2e7d32' : '#c62828',
            '& .MuiAlert-icon': {
              color: snackbarSeverity === 'success' ? '#4caf50' : '#d32f2f',
            },
          }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Signals;